import { comparePayloads, NodeId, TreeView, TreeViewNode, TraitLabel, SharedTree } from '@fluid-experimental/tree';
import { MfsValue } from '../..';
import { assertPrecondition, Result } from '../../errors';
import { getItemNode } from './nodesRead';
import { MfsValueNode, NodeKind, PropertySetMode, PROPERTY_VALUES } from './types';
import { isMfsItemNodeProperty, isMultiValuePropertyNode, isSingleValuePropertyNode } from './typeGuards';
import { MfsItemId } from '../../store/mfsTypes';
import { isMfsBaseProperty } from '../../store/mfsProperty';
import { MfsItemNode, MfsMultiValueNode } from '.';
import { hasAnyElement, LABEL_ID_PROPERTY, removeDuplicates } from '../../utility';
import { valueExistsInTraits } from './lookup';
import { logVerbose } from '../../log';

/** Gets the property if it is defined on the item; otherwise undefined. Throws if the item does not exist. */
export function tryGetItemProperty(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  propertyName: string
): MfsValue | undefined {
  const itemNode = getItemNode(tree, view, itemId);

  if (isMfsBaseProperty(propertyName)) {
    if (propertyName === 'id') {
      return itemNode.payload.id;
    }
    if (isMfsItemNodeProperty(propertyName)) {
      return itemNode.payload[propertyName];
    }
  }

  const traits = tryGetPropertyTraits(itemNode, propertyName);

  if (!traits) {
    // ok, property not found
    logVerbose('tryGetItemProperty', 'No Property found', { itemId, propertyName });
    return undefined;
  }

  return getPropertyValue(view, traits);
}

export function getPropertyNode(
  view: TreeView,
  itemNode: MfsItemNode,
  propertyKey: TraitLabel
): MfsValueNode | MfsMultiValueNode | undefined {
  const traits = itemNode.traits.get(propertyKey);

  if (!traits) {
    // ok, property doesn't exist.
    logVerbose('getPropertyNode', 'Property traits does not exist', { itemNode });
    return undefined;
  }

  const propertyNode = view.getViewNode(traits[0]);

  assertPrecondition(isMultiValuePropertyNode(propertyNode) || isSingleValuePropertyNode(propertyNode));

  return propertyNode;
}

export function tryGetPropertyTraits(itemNode: MfsItemNode, propertyKey: string): readonly NodeId[] | undefined {
  return itemNode.traits.get(propertyKey as TraitLabel);
}

export function getPropertyValue(view: TreeView, traits: readonly NodeId[]): MfsValue {
  // Always take the value from the first node, even if there are multiple of them (LWW semantics in case of race conditions).
  const propertyNode = view.getViewNode(traits[0]);
  return getValue(view, propertyNode);
}

export function getValue(view: TreeView, propertyNode: TreeViewNode): MfsValue {
  switch (propertyNode.definition) {
    case NodeKind.Array: {
      return (propertyNode.traits.get(PROPERTY_VALUES) ?? []).map((id) => view.getViewNode(id).payload);
    }
    default:
      return propertyNode.payload;
  }
}

export function getLabelProps(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId
): { label: string; labelId?: string } {
  const itemNode = getItemNode(tree, view, itemId);
  const labelIdTraits = itemNode.traits.get(LABEL_ID_PROPERTY as TraitLabel);
  const labelId = labelIdTraits ? (getPropertyValue(view, labelIdTraits) as string) : undefined;
  return { label: itemNode.payload.label, labelId };
}

export function getItemLabel(tree: SharedTree, view: TreeView, itemId: MfsItemId): string {
  const itemNode = getItemNode(tree, view, itemId);
  return itemNode.payload.label;
}

/** @returns The values except the ones that already exist among the given values or the property values in the snapshot. */
export function getUniqueValues(
  view: TreeView,
  values: MfsValue[],
  mode: PropertySetMode,
  traits?: readonly NodeId[]
): Result<MfsValue[]> {
  if (!hasAnyElement(values)) {
    // nothing to process - let it pass - ok to set to an empty array to reset a property.
    logVerbose('getUniqueValues', 'Values are all undefined, nothing to process');
    return Result.ok(values);
  }

  const uniqueValues = removeDuplicates(values);

  if (!traits || mode === 'put') {
    // if property doesn't already exist, or its being reset, nothing else to do.
    logVerbose('getUniqueValues', 'Property does not exist or is being reset, nothing to process');
    return Result.ok(uniqueValues);
  }

  // otherwise ensure the given value is not already among the values of the property in the current snapshot.
  const newUniqueValues = uniqueValues.filter(
    (v) => !valueExistsInTraits(view, v, traits, (n) => comparePayloads(v, n.payload))
  );

  if (!hasAnyElement(newUniqueValues)) {
    // no new values to add - this needs to be a different case than just passing no values at all,
    // so that we can skip it, and we do not unintentionally reset the whole array.
    logVerbose('getUniqueValues', 'Error, no new values to add');
    return Result.fail(new Error('No new values to add'));
  }

  return Result.ok(newUniqueValues);
}
